import { type FC } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';

// screen
import { formatDuration } from 'modules/dashboard/helpers/accessStats';
import type { AccessStatisticsResponse } from 'modules/dashboard/types';

interface Props {
  data?: AccessStatisticsResponse | null;
}

const HIGHLIGHT = '#E5342A';

/**
 * Một THẺ RỜI: nhãn nhỏ ở trên, số to màu đỏ ở dưới.
 *
 * Thiết kế (Figma node 6579-21712) vẽ hai thẻ tách hẳn nhau, mỗi thẻ một khung bo góc riêng và có
 * khoảng cách ở giữa — không phải một khung chia đôi bằng đường kẻ như bản dựng đầu.
 */
const Card: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      px: 2,
      py: 1.5,
      borderRadius: 2.5,
      border: '1px solid #EEF0F4',
      boxShadow: '0 2px 10px rgba(16,24,40,0.06)',
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ color: HIGHLIGHT, fontWeight: 800, fontSize: 28, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Paper>
);

/**
 * Tầng 1 của khối "Số lượt truy cập hệ thống": thời gian truy cập trung bình + số người đang
 * online. Khách vãng lai cũng thấy khối này (tài liệu: mọi người xem đầy đủ).
 */
const AccessSummaryCards: FC<Props> = ({ data }) => {
  const { formatMessage } = useIntl();

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <GroupsOutlined fontSize="small" />
        {formatMessage({ id: 'dashboard.access.sectionTitle' })}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Card
          label={formatMessage({ id: 'dashboard.access.avgDuration' })}
          value={formatDuration(data?.avg_visit_seconds)}
        />
        <Card
          label={formatMessage({ id: 'dashboard.access.onlineNow' })}
          value={data ? data.online_now.toLocaleString('vi-VN') : '—'}
        />
      </Box>
    </Box>
  );
};

export default AccessSummaryCards;
